import { QueryClient } from '@tanstack/react-query'
import { usePlatforms } from '../contexts/PlatformsContext'

// Create optimized QueryClient with performance-focused configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 10 minutes before considering it stale
      staleTime: 1000 * 60 * 10, // 10 minutes
      
      // Keep data in cache for 15 minutes
      gcTime: 1000 * 60 * 15, // 15 minutes (was cacheTime in v4)
      
      // Refetch in background every 10 minutes
      refetchInterval: 1000 * 60 * 10, // 10 minutes
      
      // Disable unnecessary refetch triggers for better performance
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      
      // Retry failed requests up to 2 times
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Enable background refetching for better UX
      refetchIntervalInBackground: true,
      
      // Optimize network mode
      networkMode: 'online',
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      networkMode: 'online',
    },
  },
})

// Query key factory for consistent caching
export const queryKeys = {
  // Dashboard related queries
  dashboard: {
    all: ['dashboard'],
    trending: () => [...queryKeys.dashboard.all, 'trending'],
    trendingByPlatform: (platform) => [...queryKeys.dashboard.trending(), platform],
    newReleases: () => [...queryKeys.dashboard.all, 'newReleases'],
    newReleasesByPlatform: (platform) => [...queryKeys.dashboard.newReleases(), platform],
    trackedApps: () => [...queryKeys.dashboard.all, 'trackedApps'],
  topPerformance: () => [...queryKeys.dashboard.all, 'topPerformance'],
    myTrackedApps: () => [...queryKeys.dashboard.all, 'myTrackedApps'],
    topSelling: () => [...queryKeys.dashboard.all, 'topSelling'],
  },
  
  // App specific queries
  apps: {
    all: ['apps'],
    detail: (id) => [...queryKeys.apps.all, 'detail', id],
    byPublisher: (publisherId) => [...queryKeys.apps.all, 'publisher', publisherId],
    search: (query) => [...queryKeys.apps.all, 'search', query],
    tracking: (params) => [...queryKeys.apps.all, 'tracking', params],
    analytics: (id, params) => [...queryKeys.apps.all, 'analytics', id, params],
  },
  
  // Category queries
  categories: {
    all: ['categories'],
    detail: (id) => [...queryKeys.categories.all, 'detail', id],
    analysis: (category) => [...queryKeys.categories.all, 'analysis', category],
    tracking: () => [...queryKeys.categories.all, 'tracking'],
    keywords: () => [...queryKeys.categories.all, 'keywords'],
    data: (category) => [...queryKeys.categories.all, 'data', category],
    groups: () => [...queryKeys.categories.all, 'groups'],
    userTracked: () => [...queryKeys.categories.all, 'userTracked'],
    search: (query) => [...queryKeys.categories.all, 'search', query],
  },
  
  // Keyword queries
  keywords: {
    all: ['keywords'],
    tracking: (keyword) => [...queryKeys.keywords.all, 'tracking', keyword],
    details: (keyword) => [...queryKeys.keywords.all, 'details', keyword],
    search: (query) => [...queryKeys.keywords.all, 'search', query],
    predefined: () => [...queryKeys.keywords.all, 'predefined'],
    groups: () => [...queryKeys.keywords.all, 'groups'],
    userTracked: () => [...queryKeys.keywords.all, 'userTracked'],
  },
  
  // Publisher queries
  publishers: {
    all: ['publishers'],
    detail: (id) => [...queryKeys.publishers.all, 'detail', id],
    analysis: (id) => [...queryKeys.publishers.all, 'analysis', id],
    apps: (id) => [...queryKeys.publishers.all, 'apps', id],
    data: (itemId) => [...queryKeys.publishers.all, 'data', itemId],
    appDetails: (appId) => [...queryKeys.publishers.all, 'appDetails', appId],
    search: (query) => [...queryKeys.publishers.all, 'search', query],
  },
  
  // User queries
  user: {
    all: ['user'],
    profile: () => [...queryKeys.user.all, 'profile'],
    settings: () => [...queryKeys.user.all, 'settings'],
    plans: () => [...queryKeys.user.all, 'plans'],
    payment: () => [...queryKeys.user.all, 'payment'],
    paymentMethods: () => [...queryKeys.user.all, 'paymentMethods'],
  },
  
  // Game Dashboard queries
  games: {
    all: ['games'],
    detail: (id) => [...queryKeys.games.all, 'detail', id],
    analytics: (id, params) => [...queryKeys.games.all, 'analytics', id, params],
  },
  
  // Platform insight queries (formerly Meta insight)
  platformInsight: {
    all: ['platformInsight'],
    topApps: () => [...queryKeys.platformInsight.all, 'topApps'],
    popular: () => [...queryKeys.platformInsight.all, 'popular'],
    mostPopular: () => [...queryKeys.platformInsight.all, 'mostPopular'],
    preOrders: () => [...queryKeys.platformInsight.all, 'preOrders'],
  },
  
  // Website (in-app) notification queries
  notifications: {
    all: ['notifications'],
    website: (params) => [...queryKeys.notifications.all, 'website', params],
  },

  // Custom insight queries
  customInsight: {
    all: ['customInsight'],
    topGrossing: () => [...queryKeys.customInsight.all, 'topGrossing'],
    topImproved: () => [...queryKeys.customInsight.all, 'topImproved'],
    hits: () => [...queryKeys.customInsight.all, 'hits'],
    topRated: () => [...queryKeys.customInsight.all, 'topRated'],
    topDownloaded: () => [...queryKeys.customInsight.all, 'topDownloaded'],
    topReviewed: () => [...queryKeys.customInsight.all, 'topReviewed'],
  },
}

// Prefetch utilities
export const prefetchUtils = {
  // Prefetch dashboard data
  prefetchDashboard: async () => {
    // This function should be called from a component that has access to platforms context
    // For now, prefetch the main trending and new releases endpoints
    const promises = [
      queryClient.prefetchQuery({
        queryKey: queryKeys.dashboard.trending(),
        queryFn: () => fetch('/api/tracker/stats/trending').then(res => res.json()),
        staleTime: 1000 * 60 * 5, // 5 minutes for prefetch
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.dashboard.newReleases(),
        queryFn: () => fetch('/api/tracker/stats/releases').then(res => res.json()),
        staleTime: 1000 * 60 * 5,
      }),
    ]
    
    return Promise.allSettled(promises)
  },
  
  // Prefetch app details
  prefetchAppDetails: async (appId) => {
    if (!appId) return
    
    return queryClient.prefetchQuery({
      queryKey: queryKeys.apps.detail(appId),
      queryFn: () => fetch(`/api/app/${appId}`).then(res => res.json()),
      staleTime: 1000 * 60 * 5,
    })
  },
  
  // Prefetch category data
  prefetchCategoryData: async (categoryId) => {
    if (!categoryId) return
    
    return queryClient.prefetchQuery({
      queryKey: queryKeys.categories.detail(categoryId),
      queryFn: () => fetch(`/api/category/${categoryId}`).then(res => res.json()),
      staleTime: 1000 * 60 * 5,
    })
  },
}

// Cache invalidation utilities
export const cacheUtils = {
  // Invalidate all dashboard queries
  invalidateDashboard: () => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.dashboard.all,
    })
  },
  
  // Invalidate specific app data
  invalidateApp: (appId) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.apps.detail(appId),
    })
  },
  
  // Clear all cache
  clearAllCache: () => {
    return queryClient.clear()
  },
  
  // Remove specific query from cache
  removeQuery: (queryKey) => {
    return queryClient.removeQueries({ queryKey })
  },
}

export default queryClient
